import { useEffect, useRef } from "react";
import { useDispatch } from "react-redux";

import {
  setSyncStatus,
} from "../app/offerWizard/offerWizardSlice";
import {
  useUpdateDmThreadMutation,
  type UpdateDmThreadParams,
} from "../app/services/dmThreads";
import type {
  OfferFormState,
  OfferWizardEntry,
} from "../components/offer-wizard/types";
import {
  mapCompensationLinesToApi,
  sumCompensationLines,
} from "../utils/offerCompensationLines";
import { normalizeAcceptPosition } from "../components/offer-wizard/roleAcceptPosition";

interface UseOfferDraftAutosaveArgs {
  threadKey: string;
  projectId: string;
  /**
   * Redux に保持されている現在の entry。`serverDmId` が未設定のときは
   * autosave は走らない（先に `createDmThread` で BE 側の draft を作る必要がある）。
   */
  entry: OfferWizardEntry | undefined;
  /** debounce 待機時間（ミリ秒）。デフォルト 1500ms */
  debounceMs?: number;
}

/**
 * `OfferFormState` と `entry.data.recipient` / `summary` を BE のドラフトスレッドに
 * debounce 同期するフック。`OfferWizardOverlay` が `createDmThread` 直後に
 * `buildOfferThreadPatchBody` を介して呼ぶ「初期 PATCH」「送信前 flush」も
 * 同じヘルパーを共有する。
 *
 * 仕様書 dm-threads.md §5 / 2026-05-09 BE 更新ガイドに追従し、以下フィールドを送信:
 *
 *  ## thread ブロック
 *  - `assigned_roles`        ← `entry.data.summary.roleLabel`
 *  - `scope`                 ← `form.scope`
 *  - `contract_amount`       ← 対価行の合計（BE auto-sync の保険として明示送信）
 *  - `advance_payment_rate`  ← `form.advancePaymentRate`
 *  - `contract_start_date`   ← `form.details.contractStartDate`
 *  - `contract_end_date`     ← `form.details.contractEndDate`
 *  - `deadline`              ← `form.deadline`
 *  - `requires_deliverable`  ← `form.requiresDeliverable`
 *  - `is_transactional`      ← `form.isTransactional`（明示時のみ）
 *  - `allow_subcontracting`  ← `form.allowSubcontracting`
 *  - `accept_position`       ← `form.acceptPosition`（明示時のみ。UPPERCASE 3 値 / null）
 *  - `initial_message`       ← `form.message`
 *
 *  ## offer_form (subcontract)
 *  - `special_provisions`    ← `form.specialProvisionsText`
 *  - `quantity`              ← `form.quantity`
 *  - `deliverable_format`    ← `form.deliverableFormat`
 *  - `delivery_method`       ← `form.details.deliveryMethod`
 *  - `delivery_location`     ← `form.details.deliveryLocation`
 *  - `valid_until`           ← `form.details.validUntil`
 *  - `recipient_email`       ← `entry.data.recipient.email`（email モード時のみ）
 *  - `compensation_lines`    ← `form.compensationLines`（non-empty 時のみ）
 *
 *  ## offer_form (master_contract)
 *  - `proposed_budget` / `contract_type` / `ownership_type` / `disclosure_rule`
 *    ← `form.masterOfferForm.*`（ハイドレートから来た場合に透過保存）
 */
export const useOfferDraftAutosave = ({
  threadKey,
  projectId,
  entry,
  debounceMs = 1500,
}: UseOfferDraftAutosaveArgs): void => {
  const dispatch = useDispatch();
  const [updateDmThread] = useUpdateDmThreadMutation();
  const lastSnapshotRef = useRef<string | null>(null);
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const dmId = entry?.serverDmId;

  useEffect(() => {
    if (!entry || !dmId) return;

    // 初回マウント時のスナップショットは「保存済み」として扱い、即時保存を抑制
    const snapshot = JSON.stringify(buildPatchBody(entry));
    if (lastSnapshotRef.current === null) {
      lastSnapshotRef.current = snapshot;
      return;
    }
    if (lastSnapshotRef.current === snapshot) return;

    if (timerRef.current) clearTimeout(timerRef.current);
    timerRef.current = setTimeout(async () => {
      const body = buildPatchBody(entry);
      lastSnapshotRef.current = JSON.stringify(body);
      const params: UpdateDmThreadParams = {
        project_id: projectId,
        dm_id: dmId,
        ...body,
      };
      try {
        dispatch(setSyncStatus({ threadKey, syncStatus: "saving" }));
        await updateDmThread(params).unwrap();
        dispatch(
          setSyncStatus({
            threadKey,
            syncStatus: "synced",
            lastSyncedAt: new Date().toISOString(),
          }),
        );
      } catch {
        dispatch(setSyncStatus({ threadKey, syncStatus: "error" }));
      }
    }, debounceMs);

    return () => {
      if (timerRef.current) clearTimeout(timerRef.current);
    };
  }, [entry, dmId, projectId, threadKey, debounceMs, dispatch, updateDmThread]);
};

// ─── 内部ヘルパー ────────────────────────────────

/**
 * `OfferWizardEntry` の現在値から `PATCH /dm/<id>` のボディに乗せるフィールドのみを抽出する。
 * `undefined` のフィールドは送らない（BE 側「未指定 = 変更なし」仕様に準拠）。
 *
 * `useOfferDraftAutosave` が debounce で呼び出すほか、`OfferWizardOverlay` から
 * 「初期 PATCH」（`createDmThread` 成功直後）「送信前 flush」（PDF 生成 / 状態遷移直前）の
 * 用途で同期的に呼び出される。
 */
export const buildOfferThreadPatchBody = (
  entry: OfferWizardEntry,
): Partial<UpdateDmThreadParams> => buildPatchBody(entry);

const buildPatchBody = (
  entry: OfferWizardEntry,
): Partial<UpdateDmThreadParams> => {
  const form = entry.data.form;
  const details = form.details;
  const body: Partial<UpdateDmThreadParams> = {};

  // ── thread: scope / role / message ──
  const assignedRoles = entry.data.summary.roleLabel;
  if (assignedRoles !== undefined) body.assigned_roles = assignedRoles;
  if (form.scope !== undefined) body.scope = form.scope;
  if (form.message !== undefined) body.initial_message = form.message;

  // ── thread: amounts ──
  // `compensation_lines` が non-empty なら BE が `Σ unit_price * quantity` を
  // `thread.contract_amount` に自動上書きする仕様。FE は防御的に同じ計算値を
  // `contract_amount` にも明示送信し、PDF 生成等で BE 自動同期前に参照される
  // タイミング問題を防ぐ。両者は同じソースから算出されるため値の競合は起きない。
  const apiLines = mapCompensationLinesToApi(form.compensationLines);
  const amount = sumCompensationLines(form.compensationLines);
  if (apiLines !== null) body.compensation_lines = apiLines;
  if (amount !== null) body.contract_amount = amount;
  if (form.advancePaymentRate !== undefined) {
    body.advance_payment_rate = form.advancePaymentRate;
  }

  // ── thread: schedule ──
  if (form.deadline !== undefined) body.deadline = form.deadline;
  if (details?.contractStartDate !== undefined) {
    body.contract_start_date = details.contractStartDate;
  }
  if (details?.contractEndDate !== undefined) {
    body.contract_end_date = details.contractEndDate;
  }

  // ── thread: workflow flags ──
  // `allow_subcontracting` / `requires_deliverable` は thread レベル必須フラグなので
  // 常に bool で送る（仕様書 §3-1-7）。フォームに値が無い場合のフォールバック解決は
  // `resolveRequiresDeliverable` 参照。
  body.allow_subcontracting = !!form.allowSubcontracting;
  body.requires_deliverable = resolveRequiresDeliverable(form);
  if (form.isTransactional !== undefined) {
    body.is_transactional = form.isTransactional;
  }
  // `accept_position` はフォームで明示選択（または明示クリア）された時だけ送る。
  // `undefined` のとき = ユーザー未操作 = 「変更なし」を維持。
  //
  // 値は必ず `normalizeAcceptPosition` を通す。ロールマスタには旧 lowercase 値
  //（`producer` / `creator` 等）が残っているレコードがあり、そのまま送ると
  // BE が 400 を返して「保存に失敗しました」になる（`UpdateDmThreadParams` 参照）。
  // 認識できない値は `null`（= 変更なし）に倒して送信をブロックする。
  if (form.acceptPosition !== undefined) {
    body.accept_position = normalizeAcceptPosition(form.acceptPosition);
  }

  // ── offer_form (subcontract) ──
  if (form.specialProvisionsText !== undefined) {
    body.special_provisions = form.specialProvisionsText;
  }
  if (form.quantity !== undefined) body.quantity = form.quantity;
  if (form.deliverableFormat !== undefined) {
    body.deliverable_format = form.deliverableFormat;
  }
  if (details?.deliveryMethod !== undefined) {
    body.delivery_method = details.deliveryMethod;
  }
  if (details?.deliveryLocation !== undefined) {
    body.delivery_location = details.deliveryLocation;
  }
  if (details?.validUntil !== undefined) {
    body.valid_until = details.validUntil;
  }
  // 送信先（受託者）の同期。`entry.data.recipient` を単一ソースに、follows /
  // email それぞれのモードで対応する API フィールドへ載せる。
  // - follows モード: `contractor_user_id` を送る（BE はドラフト中のみ受託者
  //   再割り当てを許可。同値再送は no-op）。
  // - email モード:   `recipient_email` を送る（BE 側で招待 AuthUser を解決し、
  //   creator と snapshot を同期）。
  // どちらか一方のみを送ること（両方同時送信は BE 側で 400）。
  if (
    entry.data.recipient.mode === "follows" &&
    entry.data.recipient.userId !== undefined
  ) {
    body.contractor_user_id = entry.data.recipient.userId;
  } else if (
    entry.data.recipient.mode === "email" &&
    entry.data.recipient.email !== undefined
  ) {
    body.recipient_email = entry.data.recipient.email;
  }

  // ── offer_form (master_contract) ──
  // 本 PR では入力 UI を持たないが、ハイドレートで `masterOfferForm` に値が
  // 入った場合に備えて送信透過を担保する。
  const mf = form.masterOfferForm;
  if (mf) {
    if (mf.proposedBudget !== undefined) body.proposed_budget = mf.proposedBudget;
    if (mf.contractType !== undefined) body.contract_type = mf.contractType;
    if (mf.ownershipType !== undefined) body.ownership_type = mf.ownershipType;
    if (mf.disclosureRule !== undefined) body.disclosure_rule = mf.disclosureRule;
  }

  return body;
};

/**
 * `requires_deliverable` の値を決定する。
 * フォームに明示値があればそれを使い、無ければ `deliverableFormat` の有無から
 * 後方互換ヒューリスティックで導出する。
 */
const resolveRequiresDeliverable = (form: OfferFormState): boolean => {
  if (form.requiresDeliverable !== undefined) return form.requiresDeliverable;
  return !!form.deliverableFormat && form.deliverableFormat.trim().length > 0;
};
